import { defineStore } from 'pinia'
import { ref, computed, reactive } from 'vue'
import { showToast } from 'vant'
import { apiService } from '@/api/apiService.js'
import * as audioService from '@/composables/useAudio.js'
import { useAppStore } from './app.js'
import { buildAIAudioUrl } from '@/utils/audio.js'
import { CONVERSATION_STATUS } from '@/constants/conversation.js'

/**
 * 聊天会话状态管理 - 简化版
 * 职责：统一管理所有音频状态
 */
export const useChatStore = defineStore('chat', () => {
  const appStore = useAppStore()

  // ==================== 会话状态 ====================

  const currentSession = ref(null)
  const messages = ref([])
  const qrCodeList = ref([]) // 改为数组，支持多个二维码
  const isStartingSession = ref(false)

  // ==================== 音频状态（单一数据源）====================

  const audioState = reactive({
    // 录音状态
    isRecording: false,
    recordingStartTime: null,

    // 播放状态
    isPlaying: false,
    currentAudioUrl: null,
    currentAudioType: null // 'ai' | 'user-wechat'
  })

  // ==================== 计算属性 ====================

  const hasSession = computed(() => !!currentSession.value)

  const isSessionCompleted = computed(() =>
    currentSession.value?.isCompleted || false
  )

  const currentProgress = computed(() => {
    if (!currentSession.value) return 0
    return Math.round(
      (currentSession.value.currentQuestionNumber / currentSession.value.totalQuestions) * 100
    )
  })

  // 向后兼容的计算属性
  const isRecording = computed(() => audioState.isRecording)
  const isAudioPlaying = computed(() => audioState.isPlaying)
  const currentAudioUrl = computed(() => audioState.currentAudioUrl)

  // ==================== 会话管理 ====================

  /**
   * 开始对话式问诊
   * @returns {Promise<void>}
   */
  const startSession = async () => {
    if (isStartingSession.value || currentSession.value) {
      // debug('🔄 会话已在进行中')
      return
    }

    try {
      // info('🚀 开始启动对话式问诊')
      isStartingSession.value = true
      appStore.setLoading(true)

      const response = await apiService.startSurvey()
      const sessionData = response

      // debug('📦 会话响应数据:', sessionData)

      // 创建会话对象（新结构）
      const session = {
        sessionId: sessionData.session_id,
        conversationId: sessionData.conversation_id,
        status: sessionData.status, // 新字段：对话状态
        isCompleted: sessionData.status === CONVERSATION_STATUS.COMPLETED,
        messages: [],
        startTime: new Date()
      }

      currentSession.value = session
      messages.value = []

      // info('📝 会话创建成功:', session)

      // 注意：新流程不在这里提交基础数据，而是在完成时提交

      // 添加第一个AI问题
      const questionContent = sessionData.system_message
      const audioPath = sessionData.audio_path
      const audioUrl = buildAIAudioUrl(audioPath) // 拼接完整URL

      if (questionContent && questionContent.trim()) {
        await addAIMessage(questionContent.trim(), audioUrl)
      } else {
        await addAIMessage('您好！欢迎开始问诊，请描述您的情况。', null)
      }

      appStore.setCurrentStep('chat')
    } catch (error) {
      // error('❌ 开始会话失败:', error)
      appStore.setError('开始问诊失败，请重试')
      throw error
    } finally {
      appStore.setLoading(false)
      isStartingSession.value = false
    }
  }

  /**
   * 发送文字消息
   * @param {string} text - 消息内容
   * @returns {Promise<void>}
   */
  const sendTextMessage = async (text) => {
    if (!currentSession.value || !text.trim()) return

    try {
      addUserMessage(text, 'text')

      const response = await apiService.sendTextAnswer({
        session_id: currentSession.value.sessionId,
        user_message: text.trim()
      })

      await handleSurveyResponse(response)
    } catch (error) {
      // error('❌ 发送文字消息失败:', error)
      showToast({
        type: 'fail',
        message: '发送消息失败，请重试',
        duration: 3000
      })
    }
  }

  /**
   * 添加用户消息
   * @param {string} content - 消息内容
   * @param {'text'|'audio'} type - 消息类型
   * @param {Object|null} [audioData=null] - 音频数据
   * @returns {void}
   */
  const addUserMessage = (content, type, audioData = null) => {
    if (!currentSession.value) return

    const message = {
      id: generateMessageId(),
      sessionId: currentSession.value.sessionId,
      content,
      type,
      sender: 'user',
      timestamp: new Date(),
      audioData
    }

    messages.value.push(message)
    currentSession.value.messages.push(message)
  }

  /**
   * 添加AI消息
   * @param {string} content - 消息内容
   * @param {string|null} audioUrl - 音频URL
   * @returns {Promise<Object>} 消息对象
   */
  const addAIMessage = async (content, audioUrl) => {
    if (!currentSession.value) {
      // error('❌ 无法添加AI消息：当前会话不存在')
      return
    }

    const message = {
      id: generateMessageId(),
      sessionId: currentSession.value.sessionId,
      content,
      type: 'text',
      sender: 'ai',
      timestamp: new Date(),
      audioUrl,
      isTyping: !!content
    }

    messages.value.push(message)
    currentSession.value.messages.push(message)

    // 需求（issue: chat-no-autoplay-audio）：AI消息到达时不自动播放语音，
    // 仅保留消息气泡上的"播放"按钮供用户手动触发（见 playAudio）。

    return message
  }

  /**
   * 处理对话响应（直接使用后端字段）
   * @param {Object} response - 后端响应数据
   * @returns {Promise<void>}
   */
  const handleSurveyResponse = async (response) => {
    if (!currentSession.value) return

    // 更新会话状态（但先不设置isCompleted）
    if (response.status) {
      currentSession.value.status = response.status
    }

    // debug('🔄 会话状态:', currentSession.value.status)

    // 检查是否完成对话
    if (response.status === CONVERSATION_STATUS.COMPLETED) {
      // info('✅ 对话已完成，准备获取结果')

      // 先获取对话结果（包含二维码数据）
      await fetchConversationResult()

      // 数据获取完成后，才设置完成状态（触发watch显示弹窗）
      currentSession.value.isCompleted = true

      return
    }

    // 添加下一个问题
    if (response.system_message) {
      // debug('🎵 后端返回的audio_path:', response.audio_path)
      const audioUrl = buildAIAudioUrl(response.audio_path)
      // debug('🎵 构建的audioUrl:', audioUrl)
      await addAIMessage(response.system_message.trim(), audioUrl)
    }
  }

  /**
   * 获取对话结果（直接使用后端字段）
   * @returns {Promise<void>}
   */
  const fetchConversationResult = async () => {
    try {
      // info('🔄 开始获取对话结果...')
      appStore.setLoading(true)

      const response = await apiService.getConversationResult({
        session_id: currentSession.value.sessionId
      })

      // debug('📦 病历结果:', response)

      // 使用后端返回的 save_result 单个对象
      // save_result: { success, message, data }
      // data 是 base64 字符串
      if (response.save_result && response.save_result.success && response.save_result.data) {
        const qrData = response.save_result.data
        qrCodeList.value = [{
          type: 'medical',
          qrCode: qrData.startsWith('data:') ? qrData : `data:image/png;base64,${qrData}`,
          title: '病历二维码'
        }]

        // info('✅ 二维码提取成功')
      }

    } catch (error) {
      // error('❌ 获取对话结果失败:', error)
      showToast({
        type: 'fail',
        message: '获取结果失败，请重试',
        duration: 3000
      })
    } finally {
      appStore.setLoading(false)
    }
  }

  // ==================== 录音管理（状态统一管理）====================

  /**
   * 开始录音
   * @returns {Promise<void>}
   * @throws {Error} 会话不存在或录音失败
   */
  const startRecording = async () => {
    // 检查是否可以录音
    if (audioState.isRecording) {
      // debug('🎤 已经在录音中')
      return
    }

    if (!hasSession.value || isSessionCompleted.value) {
      throw new Error('无法开始录音：没有活跃会话或会话已完成')
    }

    try {
      // debug('🎤 开始录音流程')

      // 如果正在播放音频，先停止（录音和播放互斥）
      if (audioState.isPlaying) {
        // debug('🎵 停止音频播放（录音互斥）')
        stopAudio()
      }

      // 先调用微信录音API，成功后再更新状态
      await audioService.startWechatRecording()

      // API 调用成功，更新状态
      audioState.isRecording = true
      audioState.recordingStartTime = Date.now()

      // info('✅ 录音开始成功')
    } catch (error) {
      // error('❌ 开始录音失败:', error)
      // 确保状态正确
      audioState.isRecording = false
      audioState.recordingStartTime = null
      throw error
    }
  }

  /**
   * 停止录音并发送（新流程）
   * @returns {Promise<void>}
   * @throws {Error} 录音失败或上传失败
   */
  const stopRecording = async () => {
    // debug('🎤 停止录音，当前状态:', audioState.isRecording)

    if (!audioState.isRecording) {
      // warn('🎤 当前未在录音状态')
      return
    }

    // 立即重置状态
    audioState.isRecording = false
    const startTime = audioState.recordingStartTime
    audioState.recordingStartTime = null

    try {
      // 停止微信录音并获取音频文件（Blob，新流程）
      const recordingResult = await audioService.stopWechatRecording(currentSession.value.sessionId)

      // debug('🎤 录音数据获取成功:', {
      //   type: recordingResult.type,
      //   localId: recordingResult.localId,
      //   serverId: recordingResult.serverId,
      //   fileSize: recordingResult.fileBlob?.size
      // })

      if (!(recordingResult.fileBlob instanceof Blob)) {
        throw new Error('录音文件获取失败，请稍后重试')
      }

      // 添加用户消息（保留完整的录音数据，便于后续回放等）
      addUserMessage('[语音消息]', 'audio', recordingResult)

      // 发送语音回答（新接口：multipart/form-data，包含 session_id 和 file）
      const formData = new FormData()
      formData.append('session_id', currentSession.value.sessionId)
      // 创建新的 Blob，显式指定正确的 MIME 类型（避免后端返回的 Blob type 是 application/json）
      // 使用原始音频数据，但指定正确的 MIME 类型为 audio/wav
      const audioBlob = new Blob([recordingResult.fileBlob], { type: 'audio/wav' })
      formData.append('file', audioBlob, 'audio.wav')

      const response = await apiService.sendAudioAnswer(formData)

      await handleSurveyResponse(response)

    } catch (error) {
      // error('❌ 停止录音失败:', error)

      let errorMessage = '录音失败，请重试'
      if (error.message) {
        if (error.message.includes('必填项') || error.message.includes('提供相关信息')) {
          errorMessage = '语音识别失败，请重新录音或使用文字输入'
        } else if (error.message.includes('网络') || error.message.includes('超时')) {
          errorMessage = '网络异常，请检查网络连接后重试'
        } else {
          errorMessage = error.message
        }
      }

      showToast({
        type: 'fail',
        message: errorMessage,
        duration: 4000
      })
      throw error
    }
  }

  /**
   * 取消录音
   * @returns {void}
   */
  const cancelRecording = () => {
    // debug('🎤 取消录音')

    if (!audioState.isRecording) {
      // debug('🎤 当前没有录音在进行')
      return
    }

    // 重置状态
    audioState.isRecording = false
    audioState.recordingStartTime = null

    // 停止微信录音（不关心结果）
    if (window.wx) {
      window.wx.stopRecord({})
    }

    // info('✅ 录音已取消')
  }

  // ==================== 音频播放管理（状态统一管理）====================

  /**
   * 播放音频 - 统一入口
   * @param {string} audioUrl - 音频URL
   * @param {'ai'|'user-wechat'} [audioType='ai'] - 音频类型
   * @param {boolean} [isAutoPlay=false] - 是否自动播放
   * @returns {Promise<void>}
   * @throws {Error} 播放失败
   */
  const playAudio = async (audioUrl, audioType = 'ai', isAutoPlay = false) => {
    // debug('🎵 播放音频:', { audioUrl, audioType, isAutoPlay })

    try {
      // 1. 检查是否可以播放
      if (audioState.isRecording) {
        const msg = '正在录音中，无法播放音频'
        // warn('🎵', msg)
        if (!isAutoPlay) throw new Error(msg)
        return
      }

      // 2. 自动播放权限检查
      if (isAutoPlay && !appStore.userHasInteracted) {
        // debug('🎵 自动播放被阻止（用户未交互）')
        return
      }

      // 3. 停止当前播放
      if (audioState.isPlaying) {
        stopAudio()
      }

      // 4. 开始播放
      if (audioType === 'user-wechat') {
        // 播放微信录音
        await audioService.playWechatVoice(audioUrl, {
          onPlay: () => {
            audioState.isPlaying = true
            audioState.currentAudioUrl = audioUrl
            audioState.currentAudioType = audioType
            // debug('🎵 微信录音开始播放')
          },
          onEnded: () => {
            audioState.isPlaying = false
            audioState.currentAudioUrl = null
            audioState.currentAudioType = null
            // debug('🎵 微信录音播放结束')
          },
          onError: (error) => {
            audioState.isPlaying = false
            audioState.currentAudioUrl = null
            audioState.currentAudioType = null
            // error('🎵 微信录音播放错误:', error)
          }
        })
      } else {
        // 播放服务器音频
        await audioService.playAudio(audioUrl, {
          onPlay: () => {
            audioState.isPlaying = true
            audioState.currentAudioUrl = audioUrl
            audioState.currentAudioType = audioType
            // debug('🎵 音频开始播放')
          },
          onEnded: () => {
            audioState.isPlaying = false
            audioState.currentAudioUrl = null
            audioState.currentAudioType = null
            // debug('🎵 音频播放结束')
          },
          onError: (error) => {
            audioState.isPlaying = false
            audioState.currentAudioUrl = null
            audioState.currentAudioType = null
            // error('🎵 音频播放错误:', error)
          },
          onNeedUserGesture: () => {
            // debug('🎵 需要用户交互才能播放')
            audioState.isPlaying = false
            audioState.currentAudioUrl = null
            audioState.currentAudioType = null
          }
        })
      }
    } catch (error) {
      // error('❌ 播放音频失败:', error)
      audioState.isPlaying = false
      audioState.currentAudioUrl = null
      audioState.currentAudioType = null

      // 自动播放失败时静默处理
      if (isAutoPlay && (error.name === 'NotAllowedError' || error.message?.includes('用户交互'))) {
        // debug('🎵 自动播放权限不足，静默处理')
        return
      }

      throw error
    }
  }

  /**
   * 停止音频播放
   * @returns {void}
   */
  const stopAudio = () => {
    // debug('🎵 停止音频播放')

    // 停止微信录音播放
    if (audioState.currentAudioType === 'user-wechat' && audioState.currentAudioUrl) {
      audioService.stopWechatVoice(audioState.currentAudioUrl)
    }

    // 停止服务器音频播放
    audioService.stopAudio()

    // 重置状态
    audioState.isPlaying = false
    audioState.currentAudioUrl = null
    audioState.currentAudioType = null
  }

  // ==================== 工具方法 ====================

  /**
   * 生成消息ID
   * @returns {string} 随机ID
   */
  const generateMessageId = () => {
    return Math.random().toString(36).substr(2, 9)
  }

  return {
    // 会话状态
    currentSession,
    messages,
    qrCodeList, // 改为 qrCodeList（支持多个二维码）
    hasSession,
    isSessionCompleted,
    currentProgress,

    // 音频状态（向后兼容）
    isRecording,
    isAudioPlaying,
    currentAudioUrl,

    // 会话方法
    startSession,
    sendTextMessage,
    addUserMessage,
    addAIMessage,
    handleSurveyResponse,
    fetchConversationResult, // 新增

    // 录音方法
    startRecording,
    stopRecording,
    cancelRecording,

    // 播放方法
    playAudio,
    stopAudio
  }
})
